import { useEffect } from 'react'
import { useUIStore } from '@/stores/uiStore'

/**
 * AC-21: browser autoplay policies require a user gesture before an audio
 * element may play. This hook listens once for the first pointerdown or
 * keydown anywhere on the page and flips a Zustand flag the softphone
 * checks before it tries to play the inbound ring tone.
 *
 * The flag is in-memory (per-session). A full page reload resets it —
 * intentional, because the autoplay gesture gate also resets.
 */
export function useUserInteracted(): void {
  const hasUserInteracted = useUIStore((s) => s.hasUserInteracted)
  const markUserInteracted = useUIStore((s) => s.markUserInteracted)

  useEffect(() => {
    if (hasUserInteracted) return

    const onInteract = (): void => {
      markUserInteracted()
      window.removeEventListener('pointerdown', onInteract)
      window.removeEventListener('keydown', onInteract)
    }

    window.addEventListener('pointerdown', onInteract, { once: true, passive: true })
    window.addEventListener('keydown', onInteract, { once: true })

    return () => {
      window.removeEventListener('pointerdown', onInteract)
      window.removeEventListener('keydown', onInteract)
    }
  }, [hasUserInteracted, markUserInteracted])
}
